import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Loader2,
  Monitor,
  X,
  Copy,
  Check,
  AlertCircle,
  Bell,
  Sparkles,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

/**
 * "Installa su desktop" button.
 *
 * Behaviour:
 * - Renders ONLY on real desktop browsers (no mobile UA).
 * - On Chrome / Edge / Brave desktop the `beforeinstallprompt` event lets us
 *   trigger the native install dialog with a single click.
 * - On Safari macOS, Firefox, and any other browser that doesn't support
 *   the event we fall back to an instructional modal explaining how to add
 *   the site as a desktop app via the browser address-bar action.
 */
function detect() {
  if (typeof window === "undefined") return { ok: false };
  const ua = navigator.userAgent || "";
  const isMobile =
    /Android|iPhone|iPad|iPod|Mobile|Phone|Tablet/i.test(ua) ||
    (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches);
  if (isMobile) return { ok: false };

  const isMac = /Mac|Macintosh/i.test(ua);
  const isWin = /Windows/i.test(ua);
  const isLinux = /Linux/i.test(ua) && !/Android/i.test(ua);
  const platform = isMac ? "macOS" : isWin ? "Windows" : isLinux ? "Linux" : "Desktop";

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  const isChromium =
    /Chrome|CriOS|Chromium|Edg|OPR|Brave/i.test(ua) && !/Firefox|FxiOS/i.test(ua);
  const isSafari =
    /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR/i.test(ua);

  return {
    ok: true,
    isMac,
    isWin,
    isLinux,
    platform,
    isStandalone,
    isChromium,
    isSafari,
  };
}

const PERKS = [
  { icon: Bell, text: "Notifiche desktop in tempo reale" },
  { icon: Sparkles, text: "Apertura veloce, finestra dedicata" },
  { icon: Monitor, text: "Si comporta come un'app nativa" },
];

export default function DesktopPWAInstallButton({
  className = "",
  testid = "desktop-pwa-install-cta",
  compact = false,
}) {
  const [info, setInfo] = useState(detect());
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const i = detect();
    setInfo(i);
    if (!i.ok) return;
    if (i.isStandalone) {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    const onInstalled = () => {
      setInstalled(true);
      deferredPromptRef.current = null;
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const ev = deferredPromptRef.current;
    if (!ev) {
      // No native prompt → walk the user through the manual steps
      setShowModal(true);
      return;
    }
    if (installing) return;
    setInstalling(true);
    try {
      ev.prompt();
      const { outcome } = await ev.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
      } else {
        toast("Installazione annullata. Puoi farla quando vuoi 👋");
      }
    } catch {
      toast.error("Qualcosa è andato storto. Riprova fra qualche secondo.");
    } finally {
      deferredPromptRef.current = null;
      setInstalling(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      toast.success("Link copiato!");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  if (!info.ok) return null;

  if (installed) {
    return (
      <div
        data-testid={`${testid}-installed`}
        className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[14px] font-semibold ring-1 ring-emerald-400/40 bg-emerald-500/15 text-emerald-300 ${className}`}
      >
        <CheckCircle2 size={16} strokeWidth={2.4} />
        Installata sul computer
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        disabled={installing}
        data-testid={testid}
        className={`group inline-flex items-center justify-center gap-3 rounded-2xl ${compact ? "h-16 md:h-14 px-5" : "h-16 md:h-14 px-5"} text-[14px] md:text-[15px] font-bold text-white bg-gradient-to-b from-zinc-700 to-zinc-900 ring-1 ring-white/20 active:scale-[0.97] transition-transform disabled:opacity-60 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.7)] ${className}`}
      >
        {installing ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            <span>Apertura…</span>
          </>
        ) : (
          <>
            <span className="flex-none w-10 h-10 md:w-9 md:h-9 rounded-xl flex items-center justify-center bg-white/10 ring-1 ring-white/20">
              <Monitor size={20} strokeWidth={2.2} />
            </span>
            <span className="flex-1 flex flex-col items-start leading-none gap-1 min-w-0">
              <span className="text-[10px] font-tech tracking-[0.18em] uppercase opacity-65">
                Mac · Windows
              </span>
              <span className="text-[15.5px] md:text-[15px] font-display tracking-tight whitespace-nowrap">
                Computer · 1 click
              </span>
            </span>
            <Download size={18} strokeWidth={2.6} className="flex-none opacity-75 group-hover:translate-y-0.5 transition-transform" />
          </>
        )}
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            key="desktop-pwa-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[200] flex items-center justify-center px-4"
            data-testid="desktop-pwa-modal"
          >
            <button
              type="button"
              aria-label="Chiudi"
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md rounded-3xl p-6 md:p-7 backdrop-blur-2xl"
              style={{
                background:
                  "linear-gradient(135deg, rgba(180,125,255,0.16) 0%, rgba(13,13,15,0.97) 60%)",
                border: "1px solid rgba(180,125,255,0.4)",
                boxShadow: "0 30px 80px -15px rgba(180,125,255,0.35)",
              }}
              role="dialog"
              aria-modal="true"
            >
              <button
                type="button"
                onClick={() => setShowModal(false)}
                aria-label="Chiudi"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/55 hover:text-white"
                data-testid="desktop-pwa-close"
              >
                <X size={15} />
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl flex-none flex items-center justify-center bg-gradient-to-br from-[#b47dff] to-[#8b5cf6] text-white">
                  <Monitor size={22} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-tech text-[#c9a8ff] mb-0.5">
                    Come installare
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight text-white leading-tight">
                    Aggiungi al computer
                  </h3>
                </div>
              </div>

              <div className="rounded-2xl p-3.5 bg-white/[0.04] border border-white/10 flex gap-2.5 mb-4">
                <AlertCircle size={17} className="text-[#c9a8ff] flex-none mt-0.5" />
                <p className="text-[13px] text-white/80 leading-relaxed">
                  Il tuo browser non supporta l'installazione automatica. Bastano
                  comunque 2 click — segui sotto.
                </p>
              </div>

              {info.isSafari ? (
                <ol className="space-y-2.5 mb-5">
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">1</span>
                    <span>Nella barra menu di Safari clicca <strong className="text-white">File</strong></span>
                  </li>
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">2</span>
                    <span>Seleziona <strong className="text-white">"Aggiungi al Dock"</strong> (Safari 17+ su macOS Sonoma)</span>
                  </li>
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">3</span>
                    <span>L'app appare nel Dock e nel Launchpad come app nativa</span>
                  </li>
                </ol>
              ) : (
                <ol className="space-y-2.5 mb-5">
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">1</span>
                    <span>
                      Apri questo sito in <strong className="text-white">Chrome, Edge o Brave</strong> sul tuo computer
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">2</span>
                    <span>
                      Clicca sull'icona <strong className="text-white">"Installa app" ⊕</strong> nella barra degli indirizzi (a destra)
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-[13px] text-white/80">
                    <span className="flex-none w-6 h-6 rounded-full bg-white/8 ring-1 ring-white/15 flex items-center justify-center font-display text-[12px] text-[#c9a8ff]">3</span>
                    <span>Conferma → l'app appare in Applicazioni / Start menu</span>
                  </li>
                </ol>
              )}

              {/* perks */}
              <div className="rounded-2xl bg-black/40 ring-1 ring-white/10 p-3 mb-3">
                <div className="text-[10px] uppercase tracking-[0.25em] font-tech text-white/40 mb-2">
                  Cosa ottieni
                </div>
                <ul className="space-y-1.5">
                  {PERKS.map((p) => {
                    const I = p.icon;
                    return (
                      <li key={p.text} className="flex items-center gap-2 text-[12.5px] text-white/75">
                        <I size={13} className="text-[#c9a8ff] flex-none" />
                        {p.text}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Copy link */}
              <div
                className="rounded-2xl bg-black/40 ring-1 ring-white/10 p-2 flex items-center gap-2"
                data-testid="desktop-pwa-link-box"
              >
                <span
                  className="flex-1 truncate text-[12px] font-tech text-white/65 px-2"
                  title={typeof window !== "undefined" ? window.location.origin : ""}
                >
                  {typeof window !== "undefined" ? window.location.origin : ""}
                </span>
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="desktop-pwa-copy"
                  className="flex-none inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-semibold text-white bg-gradient-to-b from-[#a78bfa] to-[#7c3aed] ring-1 ring-purple-300/40 active:scale-[0.97] transition-transform"
                >
                  {copied ? (
                    <>
                      <Check size={13} strokeWidth={2.6} /> Copiato
                    </>
                  ) : (
                    <>
                      <Copy size={13} strokeWidth={2.4} /> Copia
                    </>
                  )}
                </button>
              </div>

              <div className="text-center text-[10.5px] text-white/35 mt-4 font-tech tracking-wide uppercase">
                Tip: Chrome/Edge supportano l'install in 1 click
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
